import { useEffect, useEffectEvent, useState } from 'react'
import { useCompanion } from '../hooks/useCompanion'
import { BuddyCursor } from './BuddyCursor'
import { ActionApprovalBanner } from './ActionApprovalBanner'
import { createCtrlGesture } from '../core/ctrl-gesture'
import { createActivationChord } from '../core/activation-chord'
import type { ActionSnapshot, PointData } from '../types/companion'

/** Max ms between two chord taps to count as a double-tap */
const DOUBLE_TAP_MS = 400

/** Max ms the chord can be held and still count as a "tap" (not a hold) */
const TAP_MAX_HOLD_MS = 300

/** Delay before a held chord starts recording (avoids false start on first tap of double-tap) */
const HOLD_DELAY_MS = 250

interface CompanionOverlayProps {
  /**
   * Whether the companion is on (buddy cursor follows the mouse, hold-to-talk
   * works). Controlled by the host when given, so a UI button can flip the
   * same state as the double-tap; uncontrolled (internal state) otherwise.
   */
  enabled?: boolean
  /** Requested on/off change (double-tap of the activation chord). */
  onEnabledChange?: (enabled: boolean) => void
  /** Called when the companion is enabled/disabled or state changes */
  onActiveChange?: (active: boolean) => void
  /** Pending action snapshot from the frontend action system (optional) */
  pendingAction?: ActionSnapshot | null
  /** Approve callback from the frontend action system (optional) */
  onApproveAction?: () => void
  /** Revert callback from the frontend action system (optional) */
  onRevertAction?: () => void
}

/**
 * Resolves a data-companion-label to viewport center coordinates.
 *
 * Exported for unit testing. Called at point-display time and on every
 * resize/scroll event while a label-addressed point is active.
 *
 * @param elementLabel - The value of the data-companion-label attribute to query.
 * @returns The center coords + a 1:1 captureSize so BuddyCursor maps 1px→1px,
 *          or null when the element is not found in the DOM.
 */
/**
 * Ignore the activation chord while typing in ORDINARY inputs (a slow
 * shortcut in a filter box must not start listening) — but the companion's own surfaces
 * ([data-companion-surface]: the floating command bar, the drawer) are
 * exempt: the command bar autofocuses its textarea, and hold-to-talk must
 * work exactly there (regression 2026-08-24: gestures went dead whenever
 * the floating window was open).
 */
export function shouldIgnoreActivationTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null
  const tag = el?.tagName
  const inCompanionSurface = Boolean(el?.closest?.('[data-companion-surface]'))
  return !inCompanionSurface && (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT')
}

export function resolveElementToPoint(elementLabel: string): {
  point: PointData
  captureSize: { width: number; height: number }
} | null {
  const el = document.querySelector<HTMLElement>(`[data-companion-label="${elementLabel}"]`)
  if (!el) {
    console.debug(`[companion] element label not found: ${elementLabel}`)
    return null
  }
  const rect = el.getBoundingClientRect()
  return {
    point: {
      x: Math.round(rect.left + rect.width / 2),
      y: Math.round(rect.top + rect.height / 2),
      label: elementLabel,
    },
    // Pass viewport dims as captureSize so BuddyCursor's (x/cw)*innerWidth = x (no scaling)
    captureSize: { width: window.innerWidth, height: window.innerHeight },
  }
}

/**
 * Companion overlay, driven by the Ctrl+Alt chord (Ctrl+Option on Mac):
 *
 * - Double-tap the chord → enable/disable companion (buddy cursor)
 * - Hold the chord → push-to-talk (while enabled); release sends
 * - A single tap does nothing (it switched Ask and Build until 2026-10-16)
 */
export function CompanionOverlay({
  enabled: enabledProp,
  onEnabledChange,
  onActiveChange,
  pendingAction,
  onApproveAction,
  onRevertAction,
}: CompanionOverlayProps) {
  const {
    state,
    activePoint,
    captureSize,
    audioLevel,
    startListening,
    stopListening,
    dismissPoint,
    cancel,
  } = useCompanion()

  const [enabledState, setEnabledState] = useState(false)
  const enabled = enabledProp ?? enabledState

  // ── Label-addressed point resolution ──────────────────────────────────────
  // When activePoint carries an elementLabel, resolve it to live viewport coords
  // and pass a 1:1 captureSize to BuddyCursor. Re-resolve on resize/scroll while
  // the label point is active. Missing element → no cursor flight.
  const [resolvedPoint, setResolvedPoint] = useState<PointData | null>(null)
  const [resolvedCaptureSize, setResolvedCaptureSize] = useState<{
    width: number
    height: number
  } | null>(null)

  useEffect(() => {
    if (!activePoint?.elementLabel) {
      // Coordinate form (or null) — pass through unchanged
      setResolvedPoint(activePoint)
      setResolvedCaptureSize(null)
      return
    }

    const label = activePoint.elementLabel

    const doResolve = () => {
      const resolved = resolveElementToPoint(label)
      if (resolved) {
        setResolvedPoint(resolved.point)
        setResolvedCaptureSize(resolved.captureSize)
      } else {
        setResolvedPoint(null)
        setResolvedCaptureSize(null)
      }
    }

    doResolve()

    // Re-resolve on window geometry changes while this label point is active
    window.addEventListener('resize', doResolve)
    window.addEventListener('scroll', doResolve, true) // capture phase catches nested scrolls

    return () => {
      window.removeEventListener('resize', doResolve)
      window.removeEventListener('scroll', doResolve, true)
    }
  }, [activePoint])

  // Notify parent when companion is activated/deactivated
  useEffect(() => {
    onActiveChange?.(enabled)
  }, [enabled, onActiveChange])

  // Effect events, so a re-render mid-hold (audio-level ticks) doesn't tear
  // down the gesture and drop the release.
  const onHoldStart = useEffectEvent(() => {
    if (enabled) startListening()
  })
  const onHoldEnd = useEffectEvent(() => stopListening())
  const onCancel = useEffectEvent(() => cancel())
  const onDoubleTap = useEffectEvent(() => {
    if (enabled) cancel() // disabling — cancel anything in-flight
    setEnabledState(!enabled)
    onEnabledChange?.(!enabled)
  })
  const onEscape = useEffectEvent(() => {
    if (!enabled) return false
    cancel()
    return true
  })

  useEffect(() => {
    // The chord (core/activation-chord) turns key events into Ctrl+Alt
    // press/release; the gesture recognizer (core/ctrl-gesture) owns the
    // tap/hold/double-tap timing. Here we just map the verdicts:
    //   double-tap → enable/disable buddy · hold → push-to-talk.
    const gesture = createCtrlGesture(
      {
        onHoldStart: () => onHoldStart(),
        onHoldEnd: () => onHoldEnd(),
        onCancel: () => onCancel(),
        onDoubleTap: () => onDoubleTap(),
      },
      { doubleTapMs: DOUBLE_TAP_MS, tapMaxHoldMs: TAP_MAX_HOLD_MS, holdDelayMs: HOLD_DELAY_MS },
    )

    const chord = createActivationChord(gesture, {
      shouldIgnoreTarget: shouldIgnoreActivationTarget,
    })

    const handleKeyDown = (e: KeyboardEvent) => {
      // Escape stops audio/processing without deactivating the companion.
      if (e.key === 'Escape' && onEscape()) return
      chord.keyDown(e)
    }

    const handleKeyUp = (e: KeyboardEvent) => chord.keyUp(e)

    const handleBlur = () => chord.blur()

    window.addEventListener('keydown', handleKeyDown)
    window.addEventListener('keyup', handleKeyUp)
    window.addEventListener('blur', handleBlur)

    return () => {
      gesture.dispose()
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('keyup', handleKeyUp)
      window.removeEventListener('blur', handleBlur)
    }
  }, [])

  if (!enabled) return null

  return (
    <>
      <BuddyCursor
        state={state}
        point={resolvedPoint}
        captureSize={resolvedCaptureSize ?? captureSize}
        audioLevel={audioLevel}
        onCursorDismiss={dismissPoint}
      />
      {pendingAction && onApproveAction && onRevertAction && (
        <div
          style={{
            position: 'fixed',
            bottom: 16,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10001,
            maxWidth: 480,
            width: '90vw',
          }}
        >
          <ActionApprovalBanner
            snapshot={pendingAction}
            onApprove={onApproveAction}
            onRevert={onRevertAction}
          />
        </div>
      )}
    </>
  )
}
